/**
 * Mapping between URL slugs and ISO 3166-1 country codes.
 * Used for country/city landing page routes (/:country, /:country/:city).
 *
 * When adding a new country:
 * 1. Add slug→code entry here
 * 2. Ensure the code exists in countries.ts
 * 3. Add the slug to generate-sitemap.js
 * 4. Run `yarn prerender` if you want to pre-render the page
 */

export const slugToCode: Record<string, string> = {
  // North America
  "united-states": "US",
  "canada": "CA",
  "mexico": "MX",

  // South Asia
  "india": "IN",
  "pakistan": "PK",
  "bangladesh": "BD",
  "sri-lanka": "LK",
  "nepal": "NP",

  // Middle East & Turkey
  "turkey": "TR",
  "iran": "IR",
  "iraq": "IQ",
  "jordan": "JO",
  "lebanon": "LB",
  "syria": "SY",
  "yemen": "YE",
  "israel": "IL",

  // Gulf States
  "saudi-arabia": "SA",
  "united-arab-emirates": "AE",
  "kuwait": "KW",
  "qatar": "QA",
  "bahrain": "BH",
  "oman": "OM",

  // Central Asia
  "kazakhstan": "KZ",
  "uzbekistan": "UZ",
  "kyrgyzstan": "KG",
  "tajikistan": "TJ",
  "turkmenistan": "TM",
  "mongolia": "MN",

  // East Asia
  "china": "CN",
  "japan": "JP",
  "south-korea": "KR",
  "taiwan": "TW",

  // Southeast Asia
  "indonesia": "ID",
  "vietnam": "VN",
  "philippines": "PH",
  "malaysia": "MY",
  "thailand": "TH",
  "cambodia": "KH",
  "myanmar": "MM",

  // Eastern Europe / Caucasus / Balkans
  "russia": "RU",
  "ukraine": "UA",
  "belarus": "BY",
  "georgia": "GE",
  "armenia": "AM",
  "azerbaijan": "AZ",
  "moldova": "MD",
  "serbia": "RS",
  "kosovo": "XK",
  "north-macedonia": "MK",
  "montenegro": "ME",
  "albania": "AL",
  "bosnia-and-herzegovina": "BA",

  // Africa — North
  "nigeria": "NG",
  "egypt": "EG",
  "morocco": "MA",
  "tunisia": "TN",
  "algeria": "DZ",
  "libya": "LY",

  // Africa — Sub-Saharan
  "kenya": "KE",
  "ghana": "GH",
  "south-africa": "ZA",
  "ethiopia": "ET",
  "cameroon": "CM",
  "uganda": "UG",
  "rwanda": "RW",
  "tanzania": "TZ",

  // South America
  "brazil": "BR",
  "argentina": "AR",
  "colombia": "CO",
  "chile": "CL",
  "peru": "PE",
  "venezuela": "VE",
  "ecuador": "EC",
  "bolivia": "BO",
  "uruguay": "UY",
  "paraguay": "PY",

  // Special
  "afghanistan": "AF",
};

/** Reverse mapping: ISO code → URL slug */
export const codeToSlug: Record<string, string> = Object.fromEntries(
  Object.entries(slugToCode).map(([slug, code]) => [code, slug])
);

/** Valid Austrian city slugs (must match IDs in austrian-cities.ts) */
export const validCitySlugs = [
  "vienna",
  "graz",
  "salzburg",
  "linz",
  "innsbruck",
  "klagenfurt",
  "leoben",
  "krems",
  "hagenberg",
  "kufstein",
  "wiener-neustadt",
] as const;

/** Check if a string is a valid country slug */
export function isValidCountrySlug(slug: string): boolean {
  return slug in slugToCode;
}

/** Check if a string is a valid city slug */
export function isValidCitySlug(slug: string): boolean {
  return (validCitySlugs as readonly string[]).includes(slug);
}

/** Format a slug for display: "united-states" → "United States" */
export function formatSlug(slug: string): string {
  return slug
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}
